import { convertMarkdownToHtml } from "../markdown/markdownToHtml"

function applyOffscreenStyle(element: HTMLElement): void {
  element.style.position = "fixed"
  element.style.left = "-999999px"
  element.style.top = "-999999px"
  element.style.opacity = "0"
}

async function writeRichClipboardItem(plainText: string, html: string): Promise<boolean> {
  if (!navigator.clipboard?.write || typeof ClipboardItem === "undefined") return false
  try {
    await navigator.clipboard.write([
      new ClipboardItem({
        "text/plain": new Blob([plainText], { type: "text/plain" }),
        "text/html": new Blob([html], { type: "text/html" }),
      }),
    ])
    return true
  } catch {
    return false
  }
}

async function writePlainClipboardText(plainText: string): Promise<boolean> {
  if (!navigator.clipboard?.writeText) return false
  try {
    await navigator.clipboard.writeText(plainText)
    return true
  } catch {
    return false
  }
}

function copyViaSelectedRichElement(html: string): boolean {
  const host = document.createElement("div")
  applyOffscreenStyle(host)
  host.contentEditable = "true"
  host.innerHTML = html
  document.body.appendChild(host)
  const selection = window.getSelection()
  const restored = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null
  try {
    host.focus()
    const range = document.createRange()
    range.selectNodeContents(host)
    selection?.removeAllRanges()
    selection?.addRange(range)
    return document.execCommand("copy")
  } catch {
    return false
  } finally {
    selection?.removeAllRanges()
    if (restored) selection?.addRange(restored)
    document.body.removeChild(host)
  }
}

function copyViaHiddenTextarea(plainText: string): boolean {
  const textarea = document.createElement("textarea")
  applyOffscreenStyle(textarea)
  textarea.value = plainText
  document.body.appendChild(textarea)
  try {
    textarea.focus()
    textarea.select()
    return document.execCommand("copy")
  } catch {
    return false
  } finally {
    document.body.removeChild(textarea)
  }
}

export async function copyRichText(markdown: string): Promise<boolean> {
  if (!markdown) return false
  const html = convertMarkdownToHtml(markdown)

  if (await writeRichClipboardItem(markdown, html)) return true
  if (await writePlainClipboardText(markdown)) return true
  if (copyViaSelectedRichElement(html)) return true
  return copyViaHiddenTextarea(markdown)
}

export async function copyPlainText(plainText: string): Promise<boolean> {
  if (!plainText) return false
  if (await writePlainClipboardText(plainText)) return true
  return copyViaHiddenTextarea(plainText)
}
